import React, {
    useState, useCallback, useMemo,
} from 'react';
import PropTypes from 'prop-types';

const LoadingContext = React.createContext({
    startFetchingData: () => {},
    stopFetchingData: () => {},
    startSavingData: () => {},
    stopSavingData: () => {},
    isDataFetching: () => {},
    isDataSaving: () => {},
    isLoading: () => {},
});

export const LoadingProvider = LoadingContext.Provider;

export const LoadingConsumer = LoadingContext.Consumer;

export const LOADING_AREAS = {
    global: 'GLOBAL',
    csvExport: 'CSV_EXPORT',
    csvExportAll: 'CSV_EXPORT_ALL',
    uploadJobWizardGetSamples: 'UPLOAD_JOB_WIZARD_GET_SAMPLES',
    candidateProfile: 'CANDIDATE_PROFILE',
    uploadJobWizardShareForm: 'UPLOAD_JOB_WIZARD_SHARE_FORM',
    uploadJobWizardShareFormSuggestions: 'UPLOAD_JOB_WIZARD_SHARE_FORM_SUGGESTIONS',
    uploadJobWizardSaveRating: 'UPLOAD_JOB_WIZARD_SAVE_RATING',
    uploadJobWizardSaveSampleAction: 'UPLOAD_JOB_WIZARD_SAVE_SAMPLE_ACTION',
    uploadJobWizardClarifySearchCriteriaForm: 'UPLOAD_JOB_WIZARD_CLARIFY_SEARCH_CRITERIA_FORM',
    uploadJobWizardSamplesRefresh: 'UPLOAD_JOB_WIZARD_SAMPLES_REFRESH',
    uploadJobWizardCompleteMessageEmails: 'UPLOAD_JOB_WIZARD_COMPLETE_MESSAGE_EMAILS',
    uploadJobWizardCompleteSetFeedbackRecipients: 'UPLOAD_JOB_WIZARD_COMPLETE_SET_FEEDBACK_RECIPIENTS',
    feedbackWizardSaveAndNext: 'feedbackWizardSaveAndNext',
    feedbackWizardSaveAndPrev: 'feedbackWizardSaveAndPrev',
    positionContext: 'positionContext',
    employerWorkflowTabsData: 'employerWorkflowTabsData',
    contactCandidateFormSubmit: 'contactCandidateFormSubmit',
    rejectCandidateFormSubmit: 'rejectCandidateFormSubmit',
    botBadRatingTip: 'botBadRatingTip',
    candidateProfileFeedbackFitPanel: 'candidateProfileFeedbackFitPanel',
    viewJobUpdatePosition: 'viewJobUpdatePosition',

    uploadJobWizardLoadingGeneral: 'UPLOAD_JOB_WIZARD_LOADING_GENERAL',
    uploadJobWizardLoadCreateView: 'UPLOAD_JOB_WIZARD_LOAD_CREATE_VIEW',
    uploadJobWizardLoadSamplesView: 'UPLOAD_JOB_WIZARD_LOAD_SAMPLES_VIEW',
    uploadJobWizardUploadSample: 'UPLOAD_JOB_WIZARD_UPLOAD_SAMPLE',
    uploadJobWizardLoadPreliminaryFeedbackView: 'UPLOAD_JOB_WIZARD_LOAD_PRELIMINARY_FEEDBACK_VIEW',
    uploadJobWizardLoadCompleteMessageView: 'UPLOAD_JOB_WIZARD_LOAD_COMPLETE_MESSAGE_VIEW',
    uploadJobWizardLoadNextCandidateSample: 'UPLOAD_JOB_WIZARD_LOAD_NEXT_CANDIDATE_SAMPLE',
    candidatesBulkBackToReviewPopup: 'candidatesBulkBackToReviewPopup',
    getAllPositionBreakdowns: 'getAllPositionBreakdowns',

    campaignCreditsData: 'CAMPAIGN_CREDITS_DATA',
    campaignCreditsHistory: 'CAMPAIGN_CREDITS_HISTORY',
    walletServicePlans: 'WALLET_SERVICE_PLANS',

    addToAutoContact: 'ADD_TO_AUTO_CONTACT',

    diversityOptimizer: 'DIVERSITY_OPTIMIZER',
    diversityOptimizerShareForm: 'DIVERSITY_OPTIMIZER_SHARE_FORM',
    diversityOptimizerShareFormSuggestions: 'DIVERSITY_OPTIMIZER_SHARE_FORM_SUGGESTIONS',
    diversityOptimizerStatsRefresh: 'diversityOptimizerStatsRefresh',

    diversityReport: 'DIVERSITY_REPORT',

    contactCandidateEmailTemplates: 'contactCandidateEmailTemplates',
    sendContactCandidateEmail: 'sendContactCandidateEmail',
    sendContactCandidateLinkedin: 'sendContactCandidateLinkedin',
    sendContactCandidateInMail: 'sendContactCandidateInMail',
    contactCandidateEmailContextData: 'contactCandidateEmailContextData',
    contactCandidateEmailStatusInfo: 'contactCandidateEmailStatusInfo',
    createContactCandidateEmailTemplate: 'createContactCandidateEmailTemplate',
    updateContactCandidateEmailTemplate: 'updateContactCandidateEmailTemplate',
    deleteContactCandidateEmailTemplate: 'deleteContactCandidateEmailTemplate',
    contactCandidateCandidatesEmailsData: 'contactCandidateCandidatesEmailsData',
    candidateLinkedinConnectionStatus: 'candidateLinkedinConnectionStatus',

    editEmployerRepView: 'EDIT_EMPLOYER_REP_VIEW',
    employerPositionsView: 'EMPLOYER_POSITIONS_VIEW',

    campaignManagerTableData: 'campaignManagerTableData',
    campaignsActivityTableData: 'campaignsActivityTableData',
    campaignsActivityModalData: 'campaignsActivityModalData',

    campaignDetailsData: 'campaignDetailsData',
    campaignDetailsMessages: 'campaignDetailsMessages',
    campaignIsSaving: 'CAMPAIGN_IS_SAVING',

    confirmationModalRejectButton: 'CONFIRMATION_MODAL_REJECT_BUTTON',
    confirmationModalConfirmButton: 'CONFIRMATION_MODAL_CONFIRM_BUTTON',
    confirmationModalAdditionalButton: 'CONFIRMATION_MODAL_ADDITIONAL_BUTTON',

    candidatesList: 'candidatesList',
    candidatesCounters: 'candidatesCounters',
    candidatesListRefresh: 'candidatesListRefresh',
    candidatesListOptimize: 'candidatesListOptimize',

    getPositionRecommendations: 'getPositionRecommendations',
    acceptPositionRecommendations: 'acceptPositionRecommendations',
    declinePositionRecommendations: 'declinePositionRecommendations',
    onCampaignActivate: 'onCampaignActivate',
    onPositionActivate: 'onPositionActivate',

    applyCreditPlanBtn: 'applyCreditPlanBtn',
    applyCreditGiftBtn: 'applyCreditGiftBtn',
    onPositionClose: 'onPositionClose',

    employer: 'employer',
    candidateProfilePdf: 'candidateProfilePdf',
    metadataEmailTemplatesTemplates: 'metadataEmailTemplatesTemplates',
    metadataEmailTemplatesTemplatesTestMessage: 'metadataEmailTemplatesTemplatesTestMessage',

    industries: 'industries',
    positionCriteriaConfig: 'positionCriteriaConfig',

    activityReportExport: 'activityReportExport',
    importCandidateByLinkedin: 'importCandidateByLinkedin',
    importCandidateByLinkedinFetchSamples: 'importCandidateByLinkedinFetchSamples',

};

export const uploadJobLoadingAreas = [
    LOADING_AREAS.uploadJobWizardLoadingGeneral,
    LOADING_AREAS.uploadJobWizardLoadCreateView,
    LOADING_AREAS.uploadJobWizardLoadSamplesView,
    LOADING_AREAS.uploadJobWizardUploadSample,
    LOADING_AREAS.uploadJobWizardLoadPreliminaryFeedbackView,
    LOADING_AREAS.uploadJobWizardLoadCompleteMessageView,
];

const stateUpdate = ({ state, loadingAreas, valueToSet }) => {
    const loadingAreasArray = Array.isArray(loadingAreas)
        ? loadingAreas
        : [loadingAreas];
    return {
        ...state,
        ...((loadingAreasArray).reduce((accumulator, loadingArea) => ({
            ...accumulator,
            [loadingArea]: valueToSet,
        }), {})),
    };
};

export const LoadingApp = ({ children }) => {
    const [dataFetchingLoadingArea, setDataFetchingLoadingArea] = useState({});
    const [dataSavingLoadingArea, setDataSavingLoadingArea] = useState({});

    const isDataFetching = useCallback((loadingAreas = [LOADING_AREAS.global]) => {
        const loadingAreasArray = Array.isArray(loadingAreas)
            ? loadingAreas
            : [loadingAreas];
        return (loadingAreasArray || [])
            .some((loadingArea) => dataFetchingLoadingArea[loadingArea]);
    }, [dataFetchingLoadingArea]);

    const isDataSaving = useCallback((loadingAreas = [LOADING_AREAS.global]) => {
        const loadingAreasArray = Array.isArray(loadingAreas)
            ? loadingAreas
            : [loadingAreas];
        return (loadingAreasArray || [])
            .some((loadingArea) => dataSavingLoadingArea[loadingArea]);
    }, [dataSavingLoadingArea]);

    const startFetchingData = useCallback((loadingAreas = [LOADING_AREAS.global]) => setDataFetchingLoadingArea((state) => stateUpdate({
        state,
        loadingAreas,
        valueToSet: true,
    })), []);

    const stopFetchingData = useCallback((loadingAreas = [LOADING_AREAS.global]) => setDataFetchingLoadingArea((state) => stateUpdate({
        state,
        loadingAreas,
        valueToSet: false,
    })), []);

    const startSavingData = useCallback((loadingAreas = [LOADING_AREAS.global]) => setDataSavingLoadingArea((state) => stateUpdate({
        state,
        loadingAreas,
        valueToSet: true,
    })), []);

    const stopSavingData = useCallback((loadingAreas = [LOADING_AREAS.global]) => setDataSavingLoadingArea((state) => stateUpdate({
        state,
        loadingAreas,
        valueToSet: false,
    })), []);

    const isLoading = useCallback((loadingAreas = [LOADING_AREAS.global]) => (
        isDataFetching(loadingAreas) || isDataSaving(loadingAreas)
    ), [isDataFetching, isDataSaving]);

    const contextValue = useMemo(() => ({
        startFetchingData,
        stopFetchingData,
        startSavingData,
        stopSavingData,
        isDataFetching,
        isDataSaving,
        dataFetchingAreas: dataFetchingLoadingArea,
        isLoading,
    }), [
        startFetchingData,
        stopFetchingData,
        startSavingData,
        stopSavingData,
        isDataFetching,
        isDataSaving,
        isLoading,
    ]);

    return (
        <LoadingProvider value={contextValue}>
            {children}
        </LoadingProvider>
    );
};

LoadingApp.propTypes = {
    children: PropTypes.node,
    isDataFetchingInitial: PropTypes.bool,
    isDataSavingInitial: PropTypes.bool,
};

export default LoadingContext;
